import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { NAV } from "@/lib/nav";
import { Logo } from "@/components/Logo";
import { usePermission, useAnyVaultAccess } from "@/hooks/usePermission";
import { useAccessRequestsBadge } from "@/hooks/useAccessRequestsBadge";
import { useModuleVisibility } from "@/store/moduleVisibility";
import { Badge } from "@/components/ui/badge";
import { ChevronsLeft, ChevronsRight } from "lucide-react";

import { useAuth } from "@/store/auth";

/** Humanize a translation key when the translator returned the raw key. */
function navLabel(t, key) {
  const val = t(key);
  if (val && val !== key) return val;
  const leaf = key.split(".").pop() || key;
  return leaf
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Desktop sidebar (collapsible). Mobile uses BottomTabBar; tablet uses a Sheet drawer (Topbar handles). */
export function Sidebar({ collapsed, onToggle }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const role = (useAuth((s) => s.user?.role) || "").toUpperCase();
  // RBAC: hide modules the user can't view at all (except Home which is always shown)
  const canUsers = usePermission("users", "view");
  const canAudit = usePermission("audit", "view");
  const canViewAdmin = canUsers || canAudit;
  // Phase 8A -- module-visibility matrix filter (server-computed per role).
  // Phase 8B (bugfix): if a preview overlay is active, use it here; the real
  // matrix stays untouched for the route guard.
  const modules = useModuleVisibility((s) => s.previewModules || s.modules);
  const loaded = useModuleVisibility((s) => s.loaded);
  const anyVaultAccess = useAnyVaultAccess();   // D48-c
  const { approverPending: accessRequestsPending } = useAccessRequestsBadge();   // D55d-uat3

  const visible = NAV.filter((m) => {
    if (m.key === "home") return true;
    // Role-scoped entries (e.g. read-only Employees for FRONT_DESK,
    // CEO-only chat god view).
    if (m.showForRoles) return m.showForRoles.includes(role);
    // Legacy admin permission still gates the admin item even if the matrix says true.
    if (m.key === "admin" && !canViewAdmin) return false;
    // D55d-uat3 -- Approver-only entries (Access Requests) are hidden
    // unless the current user has pending approvals.
    if (m.approverOnly && accessRequestsPending <= 0) return false;
    // D55d-uat2 -- entries with moduleKey null are universal (my_assets).
    // Bypass the module-visibility matrix so every authed user can reach
    // them regardless of role.
    if (m.moduleKey === null || m.moduleKey === undefined) return true;
    // Matrix hasn't loaded yet -> show everything to prevent flicker; the
    // route guard still protects direct URL access.
    if (!loaded || !modules) return true;
    // D48-c: Vault is a per-category strict-gate module (D47b). The
    // coarse `modules.vault` toggle hides the entry for EMPLOYEE even
    // when they have `vault_payslips_view_own=True` (and 5 other
    // *_view_own gates). Fall through to per-category matrix OR to
    // restore self-service access. Codified pattern:
    // "sidebar gates by any-of-N matrix keys, not one broad module toggle".
    if (m.moduleKey === "vault" && anyVaultAccess) return true;
    return modules[m.moduleKey] === true;
  });

  return (
    <motion.aside
      data-testid="desktop-sidebar"
      animate={{ width: collapsed ? 80 : 264 }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
      className="hidden lg:flex fixed left-0 top-0 z-40 h-screen flex-col glass border-r border-[var(--surface-border)] overflow-hidden"
    >
      <div className="flex items-center gap-3 px-4 py-5 border-b border-[var(--surface-border)]">
        <Logo size={36} kind="mark" />
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-heading text-sm font-bold tracking-widest">SHARDA ENERGY OS</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("app.tagline")}</div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
        {visible.map((m) => {
          const Icon = m.icon;
          const active = m.key === "home" ? loc.pathname === "/" : loc.pathname.startsWith(m.to);
          const isComing = !m.enabled;
          return (
            <Link
              key={m.key}
              to={m.to}
              data-testid={`nav-${m.key}`}
              className={[
                "group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-colors duration-200",
                active
                  ? "text-white"
                  : "hover:bg-white/5 hover:translate-x-0.5 text-foreground/85 transition-transform",
              ].join(" ")}
            >
              {active && (
                <motion.div
                  layoutId="sidebar-active-indicator"
                  className="absolute inset-0 nav-indicator"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <Icon className="relative z-10 h-5 w-5 shrink-0" strokeWidth={1.75} />
              {!collapsed && (
                <span className="relative z-10 flex-1 truncate text-sm font-medium">{navLabel(t, m.labelKey)}</span>
              )}
              {m.key === "access_requests" && accessRequestsPending > 0 && (
                <span
                  data-testid="nav-badge-access_requests"
                  className={[
                    "relative z-10 inline-flex items-center justify-center rounded-full text-[10px] font-bold",
                    "bg-rose-500 text-white",
                    collapsed
                      ? "absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1"
                      : "min-w-[20px] h-[20px] px-1.5",
                  ].join(" ")}
                >
                  {accessRequestsPending > 9 ? "9+" : accessRequestsPending}
                </span>
              )}
              {!collapsed && isComing && (
                <Badge
                  variant="secondary"
                  className="relative z-10 text-[10px] bg-white/10 text-foreground/80 border-0"
                  data-testid={`badge-coming-${m.key}`}
                >
                  P{m.phase}
                </Badge>
              )}
            </Link>
          );
        })}
      </nav>

      <button
        onClick={onToggle}
        data-testid="sidebar-collapse-btn"
        className="m-3 inline-flex items-center justify-center gap-2 rounded-2xl border border-[var(--surface-border)] bg-white/5 py-2 hover:bg-white/10 text-xs uppercase tracking-widest text-foreground/80"
      >
        {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        {!collapsed && "Collapse"}
      </button>
    </motion.aside>
  );
}

export default Sidebar;
